import { styled } from '@glitz/react';
import * as React from 'react';
import { monochromeDarker } from 'svenssons/shared/theme';
import { Theme } from 'webapp/theme';
import { ZIndex } from 'webapp/z-index';
import { translate, useDynamicData } from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { CloseIcon } from 'webapp/components/icons';
import { Button } from 'webapp/components/button';
import { Flyout } from 'webapp/shared/flyout';
import { Position } from '@avensia/excite-ui';

type PropType = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export function ShippingInfoFlyout({ open, setOpen }: PropType) {
  const [shippingInfo] = useDynamicData<
    NordicNest.Api.ProductTemplatePage,
    Excite.Contentful.DocumentApiModel | undefined
  >({
    selector: data => data?.shippingInfo,
  });

  const close = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <Flyout onClose={close} position={Position.Right} visible={open} zIndex={ZIndex.Overlay}>
      <Header>
        <FlyoutHeading>{translate(t => t.productPage.shippingHeading)}</FlyoutHeading>
        <CloseButton as={Theme.Transparent} onClick={close} square aria-label={translate(t => t.ariaLabelCloseButton)}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <FlyoutBody>
        {shippingInfo && <Document elements={shippingInfo?.elements} styleDecorators={decorators} />}
      </FlyoutBody>
    </Flyout>
  );
}

const Header = styled.div({
  height: t => t.flyout.headerHeight,
  display: 'grid',
  justifyItems: t => t.flyout.headerTextPosition,
  flexShrink: 0,
  position: 'relative',
  alignItems: 'center',
  backgroundColor: t => t.lightBackground,
  gridTemplateColumns: t => `${t.sharedSpacing.l}px 1fr ${t.sharedSpacing.l}px`,
  border: { bottom: { width: 1, style: 'solid', color: t => t.borderColor } },
});

const CloseButton = styled(Button, {
  position: 'absolute',
  right: t => t.spacing.xs,
});

const FlyoutHeading = styled.h2(
  {
    fontSize: t => t.sharedFontSize.m,
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
    maxWidth: '80%',
    gridColumn: { start: 2 },
    marginBottom: 0,
  },
  overflowEllipsisDecorator,
);

const FlyoutBody = styled.div({
  padding: { xy: t => t.sharedSpacing.l },
  overflowY: 'auto',
});

const decorators: StyleDecorators = {
  h3: styled({
    fontSize: t => t.sharedFontSize.m,
    marginBottom: t => t.sharedSpacing.m,
  }),
  h4: styled({
    fontSize: t => t.sharedFontSize.s,
    marginBottom: t => t.sharedSpacing.xs,
  }),
  ul: styled({
    paddingLeft: t => t.sharedSpacing.m,
  }),
  ol: styled({
    paddingLeft: t => t.sharedSpacing.m,
  }),
  li: styled({
    marginBottom: t => t.sharedSpacing.xs,
    fontSize: t => t.sharedFontSize.xs,
    '::marker': {
      color: monochromeDarker,
    },
  }),
};
